import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { startWith, switchMap } from 'rxjs';
import { ApiEnums } from '@app/api';
import { ContextService } from '@core/providers/context/context.service';
import {
  ASK_FREQUENCY_KIND_LABEL_KEYS,
  ASK_FREQUENCY_KINDS,
  AskFrequencyKind,
  askFrequencyMaxCount,
  isRepeatingKind,
} from '../ask-frequency';
import { AskFrequencyFormGroup } from '../ask-frequency-form';

/**
 * The ask-frequency fields, bound to a group the host form owns (`createAskFrequencyGroup`), so
 * the host's validation and unsaved-changes tracking see every edit without a value accessor.
 */
@Component({
  selector: 'app-ask-frequency-field',
  templateUrl: './ask-frequency-field.component.html',
  styleUrls: ['./ask-frequency-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatButtonToggleModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    TranslatePipe,
  ],
})
export class AskFrequencyFieldComponent {
  private readonly contextService = inject(ContextService);
  private readonly lang = toSignal(inject(TranslateService).onLangChange);

  readonly group = input.required<AskFrequencyFormGroup>();
  /** Off where the counting is fixed elsewhere, as in a unit override. */
  readonly showCounting = input(true);
  /** Campaigns say "Frequency"; OMQ passes its own label. */
  readonly labelKey = input('SHARED.ASK_FREQUENCY.FIELD_LABEL');
  /** `halves` lines the field up with a two-column form row, as on the OMQ form. */
  readonly layout = input<'inline' | 'halves'>('inline');

  protected readonly kinds = ASK_FREQUENCY_KINDS;
  protected readonly kindLabelKeys = ASK_FREQUENCY_KIND_LABEL_KEYS;
  protected readonly countingScopes = ApiEnums.CountingScope;
  protected readonly unitParams = computed(() => {
    this.lang();
    return this.contextService.getAccountChildrenNameParams();
  });

  private readonly kind = toSignal(
    toObservable(this.group).pipe(
      switchMap(group =>
        group.controls.kind.valueChanges.pipe(startWith(group.controls.kind.value))
      )
    ),
    { initialValue: AskFrequencyKind.EVERY_TIME }
  );

  protected readonly isRepeating = computed(() => isRepeatingKind(this.kind()));
  protected readonly maxCount = computed(() => askFrequencyMaxCount(this.kind()));
  protected readonly isCountingVisible = computed(
    () => this.showCounting() && this.kind() !== AskFrequencyKind.EVERY_TIME
  );
}
